import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { RequestError } from "@/components/tracking/TrackingLayout";

const ITEMS = ["notMedical", "ownPhotos", "noNudity", "legal"];

export const PhotoConsent = () => {
  const { t } = useTranslation();
  const { checkUserAuth } = useAuth();
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const allChecked = ITEMS.every((item) => checked[item]);
  const accept = async () => {
    setSaving(true); setError("");
    try { await base44.auth.acceptPhotoConsent(); await checkUserAuth(); }
    catch (failure) { setError(failure.message); setSaving(false); }
  };
  return <div className="space-y-5" data-testid="photo-consent">
    <RequestError error={error} testId="photo-consent-error" />
    <div className="bg-card border border-primary/40 rounded-xl p-5 space-y-3">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-6 h-6 text-primary shrink-0" />
        <h2 className="text-base md:text-lg font-semibold">{t("consent.title")}</h2>
      </div>
      <p className="text-sm text-muted-foreground">{t("consent.subtitle")}</p>
    </div>

    <div className="bg-yellow-900/20 border border-yellow-700/40 rounded-xl p-4 flex gap-3" data-testid="photo-consent-nudity-warning">
      <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />
      <p className="text-yellow-200/90 text-xs leading-relaxed">{t("consent.nudityWarning")}</p>
    </div>

    <div className="space-y-3">
      {ITEMS.map((item) => <label key={item} htmlFor={`consent-${item}`} className="bg-card border border-border rounded-xl p-4 flex gap-3 items-start cursor-pointer hover:border-primary/40 transition-colors">
        <Checkbox id={`consent-${item}`} data-testid={`consent-check-${item}`} checked={!!checked[item]} onCheckedChange={(value) => setChecked((previous) => ({ ...previous, [item]: !!value }))} className="mt-0.5" />
        <span className="text-sm text-foreground/90 leading-relaxed">{t(`consent.items.${item}`)}</span>
      </label>)}
    </div>

    <p className="text-xs text-muted-foreground">
      {t("consent.readMore")}{" "}
      <Link to="/privacy" data-testid="consent-privacy-link" className="text-primary underline">{t("account.privacy")}</Link>
      {" · "}
      <Link to="/terms" data-testid="consent-terms-link" className="text-primary underline">{t("account.terms")}</Link>
    </p>

    <Button onClick={accept} disabled={!allChecked || saving} data-testid="photo-consent-accept" className="w-full h-12 text-base font-semibold">
      {saving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}{t("consent.accept")}
    </Button>
  </div>;
};
